import { assertEvent, setup } from 'xstate5';

import {
  type FavoritesMachineContext,
  type FavoritesMachineEvents,
  type FavoritesMachineInput,
  type FavoriteWithId,
} from './favorites-machine.types';
import { createFavoritesMachineActions } from './setup/favorites-machine.actions';
import { createFavoritesMachineActors } from './setup/favorites-machine.actors';
import { createFavoritesMachineGuards } from './setup/favorites-machine.guards';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A state machine to manage the user's favorites with pagination support.
 *
 * NOTE: The machine is also "restartable", which means it can be restarted
 *       at any point during its flow by resetting the context and fetching
 *       the initial favorites.
 *
 * NOTE: The machine requires external services to retrieve favorites.
 *
 * NOTE: The initial state can be passed to the machine as an argument, however,
 *       it is usually used for testing.
 */
export const createFavoritesMachine = <Favorite extends FavoriteWithId>(
  initialState:
    | 'waiting'
    | 'idle'
    | 'fetchingInitialFavorites'
    | 'fetchingMoreFavorites' = 'waiting',
) => {
  return setup({
    types: {
      input: {} as FavoritesMachineInput<Favorite>,
      context: {} as FavoritesMachineContext<Favorite>,
      events: {} as FavoritesMachineEvents,
    },
    actions: createFavoritesMachineActions<Favorite>(),
    actors: createFavoritesMachineActors<Favorite>(),
    guards: createFavoritesMachineGuards<Favorite>(),
  }).createMachine({
    id: 'favorites-state-machine',
    context: ({ input }) => ({
      favorites: [],
      page: 0,
      canFetchMoreFavorites: false,
      ...input,
    }),
    initial: initialState,
    states: {
      waiting: {
        description: 'An empty state used for the initial setup.',
      },
      idle: {
        id: 'idle',
        description: 'An idle state that awaits the next event.',
        on: {
          REFETCH_FAVORITES: {
            description: 'Refetch current favorites.',
            target: '#refetching-favorites',
          },
          FETCH_MORE_FAVORITES: {
            description: 'Fetch more favorites if possible.',
            target: '#fetching-more-favorites',
            actions: ['increasePage'],
            guard: 'checkIfCanFetchMoreFavorites',
          },
          TOGGLE_FAVORITE: {
            target: '#toggling-favorite',
          },
        },
      },
      fetchingInitialFavorites: {
        id: 'fetching-initial-favorites',
        initial: 'favorites',
        states: {
          favorites: {
            description: 'A state for fetching initial favorites.',
            invoke: {
              id: 'fetchFavorites',
              src: 'fetchFavorites',
              input: ({ context }) => context,
              onDone: {
                target: '#idle',
                actions: [{ type: 'onFetchFavoritesDone', params: ({ event }) => event.output }],
              },
              onError: {
                actions: [
                  {
                    type: 'logFetchFavoritesError',
                    params: ({ event }) => ({ error: event.error }),
                  },
                ],
              },
            },
          },
        },
      },
      refetchingFavorites: {
        id: 'refetching-favorites',
        initial: 'favorites',
        states: {
          favorites: {
            description: 'A state for refetching current favorites.',
            invoke: {
              id: 'fetchFavorites',
              src: 'fetchFavorites',
              input: ({ context }) => context,
              onDone: {
                target: '#idle',
                actions: [{ type: 'onFetchFavoritesDone', params: ({ event }) => event.output }],
              },
              onError: {
                actions: [
                  {
                    type: 'logFetchFavoritesError',
                    params: ({ event }) => ({ error: event.error }),
                  },
                ],
              },
            },
          },
        },
      },
      fetchingMoreFavorites: {
        id: 'fetching-more-favorites',
        initial: 'favorites',
        states: {
          favorites: {
            description: 'A state for fetching more favorites.',
            invoke: {
              id: 'fetchFavorites',
              src: 'fetchFavorites',
              input: ({ context }) => context,
              onDone: {
                target: '#idle',
                actions: [{ type: 'onFetchFavoritesDone', params: ({ event }) => event.output }],
              },
              onError: {
                actions: [
                  {
                    type: 'logFetchFavoritesError',
                    params: ({ event }) => ({ error: event.error }),
                  },
                ],
              },
            },
          },
        },
      },
      togglingFavoriteState: {
        id: 'toggling-favorite',
        invoke: {
          id: 'toggleFavorite',
          src: 'toggleFavorite',
          input: ({ context, event }) => {
            assertEvent(event, 'TOGGLE_FAVORITE');

            return { context, event };
          },
          onDone: {
            target: '#idle',
            actions: [
              {
                type: 'onToggleFavoriteDone',
                params: ({ event }) => ({ toggledFavorite: event.output }),
              },
            ],
          },
          onError: '#idle',
        },
      },
    },
    on: {
      START: {
        description:
          'Starts/restarts the machine by fetching the initial favorites and clearing the state.',
        target: '#fetching-initial-favorites',
        actions: ['resetContext'],
      },
    },
  });
};
